import React, { useCallback, useState } from 'react';
import { AppLayout, Header } from '@amzn/awsui-components-react';
import Board, { BoardProps } from '@amzn/awsui-board-components/board';
import { ApplicationState } from 'modules/app/store';
import { useSelector } from 'react-redux';
import { CalendarIcon } from './Icons';

import styles from './Home.module.scss';
import {
    Card,
    CreateSpendDistribution,
    SoftClosePOs,
    ConfirmReceipts,
    ReceivePOLines,
    ViewRBNI,
    POsNeedingAttention,
    TopAccounts,
    CardBaseProps
} from '../Cards/Card';
import { homePanels } from './HelpPanels';
import { useHelpPanel } from 'modules/core/hooks';
import { ErrorRouter } from 'modules/error/components';
import { useTranslation } from 'react-i18next';
import { AppConfig, logger } from 'modules/core';

type BoardItems = ReadonlyArray<BoardProps.Item>;

const cards = {
    [Card.CreateSpendDistribution]: (props: CardBaseProps) => <CreateSpendDistribution {...props} />,
    [Card.SoftClosePOs]: (props: CardBaseProps) => <SoftClosePOs {...props} />,
    [Card.ConfirmReceipts]: (props: CardBaseProps) => <ConfirmReceipts {...props} />,
    [Card.ReceivePOLines]: (props: CardBaseProps) => <ReceivePOLines {...props} />,
    [Card.ViewRBNI]: (props: CardBaseProps) => <ViewRBNI {...props} />,
    [Card.POsNeedingAttention]: (props: CardBaseProps) => <POsNeedingAttention {...props} />,
    [Card.TopAccounts]: (props: CardBaseProps) => <TopAccounts {...props} />
};

const save = (key: string, value: BoardItems) => {
    localStorage.setItem(key, JSON.stringify(value));
};

const load = (key: string): BoardItems | undefined => {
    const buster = AppConfig.homeItemsCacheBuster;
    const storedBuster = localStorage.getItem(`${key}-buster`);
    if (buster && buster !== storedBuster) {
        localStorage.removeItem(key);
        localStorage.setItem(`${key}-buster`, buster);
    }

    const value = localStorage.getItem(key);
    try {
        if (!value) {
            return undefined;
        }
        return JSON.parse(value);
    } catch (e) {
        logger.warn(
            `⚠️ The ${key} value that is stored in localStorage is incorrect. Try to remove the value ${key} from localStorage and reload the page`
        );
    }
};

export const defaultItems: BoardItems = [
    { id: Card.CreateSpendDistribution, data: {} },
    { id: Card.SoftClosePOs, data: {} },
    { id: Card.ConfirmReceipts, data: {} },
    { id: Card.ReceivePOLines, data: {} },
    // Not using Infinity for columnSpan because items needs to be JSON-serializable
    { id: Card.TopAccounts, columnSpan: 999, rowSpan: 6, data: {} }
    // { id: Card.ViewRBNI, data: {} }, // disabled for milestone 1
    // { id: Card.POsNeedingAttention, data: {} } // disabled for milestone 1
];

export const Home = () => {
    const [t] = useTranslation('home');

    const userFirstName = useSelector((state: ApplicationState) => state.user.firstName);
    const applicationHealth = useSelector((state: ApplicationState) => state.applicationHealth);
    const { panel, onPanelChange, dismissPanel } = useHelpPanel(homePanels);
    const widgetItemsKey = 'receiving-dashboard-widgets-layout';
    const [items, setItems] = useState(() => load(widgetItemsKey) ?? defaultItems);

    const handleLayoutChange = useCallback((newValue: BoardItems) => {
        setItems(newValue);
        save(widgetItemsKey, newValue);
    }, []);

    const content = applicationHealth.showError ? (
        <ErrorRouter errorStatusCode={applicationHealth.errorCode} dashboardError={applicationHealth.accessError} />
    ) : (
        <>
            <Header variant="h1">
                {t('welcome')}, {userFirstName ?? 'Guest'}
            </Header>
            <Header variant="h3">
                <div className={styles.iconContainer}>
                    <CalendarIcon />
                    <b className={styles.iconText}>
                        {/* Actually computing the days left for MEC is out of scope for milestone 1 */}
                        {new Date().toLocaleDateString('en-US', {
                            weekday: 'long',
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                        })}
                    </b>
                </div>
            </Header>
            <Board
                items={items}
                renderItem={(item, _actions) => cards[item.id as Card]({ onPanelChange })}
                onItemsChange={e => handleLayoutChange(e.detail.items)}
                i18nStrings={i18nStrings}
                empty={<></>}
            />
        </>
    );

    return (
        <AppLayout
            content={content}
            contentType="cards"
            navigationHide={true}
            toolsOpen={panel !== undefined}
            toolsHide={panel === undefined}
            tools={panel}
            onToolsChange={dismissPanel}
            headerSelector="#merp-nav"
        />
    );
};

/* istanbul ignore next */
const i18nStrings = {
    navigationAriaLabel: 'navigationAriaLabel', // deprecated
    navigationItemAriaLabel: () => 'navigationItemAriaLabel', // deprecated
    liveAnnouncementDndStarted: () => 'liveAnnouncementDndStarted',
    liveAnnouncementDndCommitted: () => 'liveAnnouncementDndCommitted',
    liveAnnouncementDndDiscarded: () => 'liveAnnouncementDndDiscarded',
    liveAnnouncementDndItemInserted: () => 'liveAnnouncementDndItemInserted',
    liveAnnouncementDndItemReordered: () => 'liveAnnouncementDndItemReordered',
    liveAnnouncementDndItemResized: () => 'liveAnnouncementDndItemResized',
    liveAnnouncementItemRemoved: () => 'liveAnnouncementItemRemoved'
};
